import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import {
  getContactoComercial, archivarContactoComercial, restaurarContactoComercial, deleteContactoComercial,
} from '../../api/client'
import { puede, mensajeError } from './comercialLogica'
import { urlSitio } from './organizacionesLogica'
import { quien } from './comercialUtiles'
import { Seccion, Dato, Registro, MenuMas, AvisoFicha } from './ComercialFichaPartes'
import ComercialPanel from './ComercialPanel'
import ComercialChip from './ComercialChip'
import ComercialCopiable from './ComercialCopiable'
import ComercialContactoForm from './ComercialContactoForm'
import ComercialVinculosContacto from './ComercialVinculosContacto'

// A contact's card in the side panel, in the organization's three modes:
//   consulta -- tabs Datos, Organizaciones and Registro, with its actions;
//   edición  -- "Editar" turns the same panel into the form;
//   creación -- id 'nueva' (?contacto=nueva, maybe &desde=<org id>): the form at once.
// Saving goes back to consulta (a new one becomes its own card); cancelling
// goes back to the card, or leaves the panel when creating.
//   onCambio(): something was saved, linked, archived, restored or deleted (the list reloads)
export default function ComercialFichaContacto({ id, desde, abrir, reemplazar, atras, cerrar, onCambio }) {
  const { t } = useTranslation('comercial')
  const { user } = useAuth()
  const puedeEditar = puede(user, 'comercial_editar')
  const puedeEliminar = puede(user, 'comercial_configurar')
  const nueva = id === 'nueva'
  const [c, setC] = useState(null)
  const [error, setError] = useState('')
  const [editando, setEditando] = useState(false)
  const [pestana, setPestana] = useState('datos')
  const [aviso, setAviso] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [volverSub, setVolverSub] = useState(null)      // while the form creates an organization: back to the contact
  const enSubformulario = useCallback((volver) => setVolverSub(() => volver), [])
  const salir = atras || cerrar

  // Every link, inactive ones too (the Organizaciones tab shows them, and deleting needs none).
  const cargar = useCallback(() => getContactoComercial(id, { inactivos: true }), [id])
  useEffect(() => {
    if (nueva) return undefined
    let vivo = true
    cargar().then(d => vivo && setC(d)).catch(e => vivo && setError(mensajeError(e, t, t('panel.cargaFallo'))))
    return () => { vivo = false }
  }, [cargar, nueva, t])
  const recargar = async () => {
    try { setC(await cargar()) } catch (e) { setError(mensajeError(e, t, t('panel.cargaFallo'))) }
    onCambio?.()
  }

  // --- Creating and editing ---
  if (nueva || editando) {
    return (
      <ComercialPanel titulo={volverSub ? t('form.nueva') : nueva ? t('formContacto.nuevo') : t('formContacto.editar')}
        onCerrar={cerrar} onAtras={volverSub || atras}
        encabezado={!volverSub && !nueva && c && <p className="text-xs text-gray-500 truncate">{c.nombre_completo}</p>}>
        {nueva && !puedeEditar
          ? <p className="text-sm text-gray-500">{t('formContacto.sinFuncion')}</p>
          : <ComercialContactoForm contacto={nueva ? null : c} desde={nueva ? desde : null}
              onSubformulario={enSubformulario} onOrganizacionCreada={() => onCambio?.()}
              onGuardado={async ficha => {
                onCambio?.()
                if (nueva) reemplazar('contacto', ficha.id)
                else { setEditando(false); setAviso(null); setC(await cargar().catch(() => ficha)) }
              }}
              onCancelar={() => (nueva ? salir() : setEditando(false))} />}
      </ComercialPanel>
    )
  }

  // --- Consulta ---
  const accion = async (llamar) => {
    setOcupado(true)
    setAviso(null)
    try {
      await llamar()
      await recargar()
    } catch (err) {
      setAviso({ texto: mensajeError(err, t, t('catalogo.fallo')) })
    } finally {
      setOcupado(false)
    }
  }
  const archivar = () => {
    if (!window.confirm(t('fichaContacto.archivarConfirm', { nombre: c.nombre_completo }))) return
    accion(() => archivarContactoComercial(c.id))
  }
  const restaurar = () => accion(() => restaurarContactoComercial(c.id))
  const eliminar = async () => {
    if (!window.confirm(t('fichaContacto.eliminarConfirm', { nombre: c.nombre_completo }))) return
    setOcupado(true)
    setAviso(null)
    try {
      await deleteContactoComercial(c.id)
      onCambio?.()
      salir()
    } catch (err) {
      const d = err.response?.data?.detail
      setAviso({ texto: d?.code === 'tiene_vinculos' ? t('fichaContacto.eliminarConVinculos') : mensajeError(err, t, t('catalogo.fallo')) })
      setOcupado(false)
    }
  }

  const acciones = c && (puedeEditar || puedeEliminar) && (
    <>
      {puedeEditar && (
        <button type="button" onClick={() => { setAviso(null); setEditando(true) }} disabled={ocupado}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {t('ficha.editar')}
        </button>
      )}
      <MenuMas disabled={ocupado} opciones={[
        puedeEditar && (c.archivado
          ? { texto: t('ficha.restaurar'), onClick: restaurar }
          : { texto: t('ficha.archivar'), onClick: archivar }),
        puedeEliminar && {
          texto: t('ficha.eliminar'), onClick: eliminar, peligro: true,
          deshabilitada: c.vinculos.length > 0 ? t('fichaContacto.eliminarConVinculosCorto') : null,
        },
      ].filter(Boolean)} />
    </>
  )

  return (
    <ComercialPanel titulo={c?.nombre_completo || (error ? t('panel.ficha') : '')} cargando={!c && !error}
      onCerrar={cerrar} onAtras={atras} acciones={acciones} pestana={pestana} onPestana={setPestana}
      pestanas={[
        { id: 'datos', label: t('panel.datos') },
        { id: 'organizaciones', label: t('panel.organizaciones') },
        { id: 'registro', label: t('panel.registro') },
      ]}
      encabezado={c && (c.etiquetas.length > 0 || c.archivado) && (
        <div className="flex flex-wrap gap-1">
          {c.etiquetas.map(x => <ComercialChip key={x.id} variante="etiqueta">{x.nombre}</ComercialChip>)}
          {c.archivado && (
            <ComercialChip variante="archivado" title={quien(t, c.archivado_por, c.archivado_en)}>{t('chips.archivado')}</ComercialChip>
          )}
        </div>
      )}>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <AvisoFicha aviso={aviso} onCerrar={() => setAviso(null)} abrir={abrir} />
      {c && pestana === 'datos' && <Datos c={c} abrir={abrir} />}
      {c && pestana === 'organizaciones' && (
        <ComercialVinculosContacto c={c} abrir={abrir} puedeEditar={puedeEditar} onCambio={recargar} />
      )}
      {c && pestana === 'registro' && (
        <Registro r={c} etiquetaCreacion={t('fichaContacto.creado')}
          archivo={{ si: c.archivado, por: c.archivado_por, en: c.archivado_en, etiqueta: t('chips.archivado') }} />
      )}
    </ComercialPanel>
  )
}

function Datos({ c, abrir }) {
  const { t } = useTranslation('comercial')
  const linkedin = urlSitio(c.linkedin)
  return (
    <div className="space-y-5">
      <Seccion titulo={t('ficha.identificacion')}>
        <Dato etiqueta={t('camposContacto.nombre')}>{c.nombre}</Dato>
        <Dato etiqueta={t('camposContacto.apellidos')}>{c.apellidos}</Dato>
      </Seccion>
      <Seccion titulo={t('fichaContacto.informacionContacto')}>
        <Dato etiqueta={t('camposContacto.correos')}>
          {c.correos.length > 0 && <Medios filas={c.correos} tipo="correo" />}
        </Dato>
        <Dato etiqueta={t('camposContacto.telefonos')}>
          {c.telefonos.length > 0 && <Medios filas={c.telefonos} tipo="telefono" />}
        </Dato>
        {linkedin && (
          <Dato etiqueta={t('camposContacto.linkedin')}>
            <a href={linkedin} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:underline break-all">{c.linkedin}</a>
          </Dato>
        )}
      </Seccion>
      {c.vinculos_activos.length > 0 && (
        <Seccion titulo={t('fichaContacto.organizacion')}>
          <ul className="space-y-1">
            {c.vinculos_activos.map(v => (
              <li key={v.id} className="text-sm">
                <button type="button" onClick={() => abrir('org', v.organizacion_id)}
                  className={`text-left hover:underline ${v.organizacion.archivada ? 'text-gray-400' : 'text-gray-900'}`}>
                  {v.ruta}
                </button>
                {v.cargo && <span className="text-gray-500"> · {v.cargo}</span>}
              </li>
            ))}
          </ul>
        </Seccion>
      )}
      {c.etiquetas.length > 0 && (
        <Seccion titulo={t('camposContacto.etiquetas')}>
          <div className="flex flex-wrap gap-1">{c.etiquetas.map(x => <ComercialChip key={x.id} variante="etiqueta">{x.nombre}</ComercialChip>)}</div>
        </Seccion>
      )}
      {c.observaciones && (
        <Seccion titulo={t('campos.descripcion')}>
          <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{c.observaciones}</p>
        </Seccion>
      )}
    </div>
  )
}

// Emails or phones: the principal first and marked; every one copies itself on
// click. Their saved descriptions aren't shown (the form no longer asks for them).
function Medios({ filas, tipo }) {
  const { t } = useTranslation('comercial')
  const orden = [...filas].sort((a, b) => Number(b.principal) - Number(a.principal))
  return (
    <ul className="space-y-0.5">
      {orden.map(x => (
        <li key={x.id} className="flex flex-wrap items-baseline gap-x-1.5">
          <ComercialCopiable valor={x.valor} tipo={tipo} className="text-gray-800" />
          {x.principal && filas.length > 1 && <span className="text-xs text-amber-600">★ {t('camposContacto.principal').toLowerCase()}</span>}
        </li>
      ))}
    </ul>
  )
}
